import { useState, memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, RefreshCcw } from "lucide-react";
import {
  getGetInteractiveMaterialQueryKey,
  useHandleInteractiveMaterialGeneratorWebhook,
  useFinalizeInteractiveMaterial,
  useGetInteractiveMaterial,
} from "@packages/api/madlenAPI";
import {
  DragTextItem,
  InteractiveMaterialFlashcards,
  InteractiveMaterialGlossary,
  InteractiveMaterialResponseDtoGeneratedQuizQuestionsItem,
  InteractiveMaterialResponseDtoGeneratedVideoQuestionsItem,
  InteractiveMaterialResponseDtoStatus,
} from "@packages/api/madlenAPI.schemas";
// import TranscriptTab from "../../../components/interactive-materials/TranscriptTab";
import GlossaryTab from "../../../components/interactive-materials/GlossaryTab";
import WelcomeTab from "../../../components/interactive-materials/WelcomeTab";
import QuizQuestionTab from "../../../components/interactive-materials/QuizQuestionTab";
import { FlashCardTab } from "@features/tools/components/interactive-materials/FlashCardTab";
import { useQueryClient } from "@tanstack/react-query";
import ReviewTab from "@features/tools/components/interactive-materials/ReviewTab";
import DragAndDropTab from "@features/tools/components/interactive-materials/DragAndDropTab";
import { Text, Button } from "@core/index";
import LoadingContainer from "@packages/components/Loading";
import VideoQuestionTab from "@features/tools/components/interactive-materials/VideoQuestionTab";
import SummaryTab from "@features/tools/components/interactive-materials/SummaryTab";

type Step =
  | "video"
  | "glossary"
  | "questions"
  | "videoQuestions"
  | "flashcards"
  | "review"
  | "dragAndDrop"
  | "summary";

interface ErrorStateProps {
  error: Error | null;
  onRetry: () => void;
}

const ErrorState = memo(({ error, onRetry }: ErrorStateProps) => {
  const { t } = useTranslation();

  return (
    <div className="min-h-[400px] flex flex-col items-center justify-center p-4 sm:p-8">
      <div className="relative w-full max-w-lg">
        <div className="absolute inset-0 bg-rose-500/20 dark:bg-rose-500/10 blur-xl rounded-full" />
        <div className="relative bg-white/90 dark:bg-gray-800/90 rounded-2xl p-6 sm:p-8 shadow-xl border border-rose-100 dark:border-rose-900">
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="p-4 bg-rose-100 dark:bg-rose-900/50 rounded-full animate-bounce-slow">
              <AlertTriangle
                size={32}
                className="text-rose-600 dark:text-rose-400"
              />
            </div>
            <Text variant="h2" className="!text-rose-600 dark:!text-rose-400">
              {t("interactive_material.error.loadingError")}
            </Text>
            <Text variant="p" status="subtle" size="base" className="max-w-md">
              {error?.message ||
                t("interactive_material.error.unexpectedError")}
            </Text>
            <Button
              size="md"
              bgColor="bg-rose-100 hover:bg-rose-200 dark:bg-rose-900/50 dark:hover:bg-rose-900/70"
              rounded="lg"
              textColor="text-rose-600 dark:text-rose-400"
              onClick={onRetry}
              className="group flex items-center gap-2"
            >
              <RefreshCcw
                size={16}
                className="group-hover:rotate-180 transition-transform duration-500"
              />
              {t("common.retry")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

const StepIndicator = memo(
  ({
    currentStep,
    totalSteps,
  }: {
    currentStep: number;
    totalSteps: number;
  }) => {
    return (
      <div
        className="flex items-center justify-center space-x-2 m:mb-8"
        data-testid="continue-button"
      >
        {Array.from({ length: totalSteps }).map((_, index) => (
          <div
            key={index}
            className={`w-2 sm:w-3 h-2 sm:h-3 rounded-full transition-all duration-300 ${
              index < currentStep
                ? "bg-rose-500"
                : index === currentStep
                  ? "bg-rose-500 ring-4 ring-rose-200 dark:ring-rose-900"
                  : "bg-gray-200 dark:bg-gray-700"
            }`}
          />
        ))}
      </div>
    );
  },
);

const StepNavigation = memo(
  ({
    currentStep,
    totalSteps,
    onNext,
    onPrev,
    isNextDisabled,
  }: {
    currentStep: number;
    totalSteps: number;
    onNext: () => void;
    onPrev: () => void;
    isNextDisabled?: boolean;
  }) => {
    const { t } = useTranslation();

    return (
      <div className="mt-4">
        {/* Desktop View */}
        <div className="hidden sm:flex justify-between items-center">
          <Button
            onClick={onPrev}
            bgColor="bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
            rounded="lg"
            size="md"
            variant="solid"
          >
            {currentStep === 0 ? t("common.exit") : t("common.previous")}
          </Button>
          <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />
          <Button
            onClick={onNext}
            disabled={isNextDisabled}
            variant="solid"
            size="md"
            rounded="lg"
            bgColor={
              isNextDisabled
                ? "opacity-50 cursor-not-allowed"
                : "bg-rose-500 hover:bg-rose-600"
            }
            textColor="text-white"
          >
            {currentStep === totalSteps - 1
              ? t("interactive_material.assignStudents")
              : t("common.next")}
          </Button>
        </div>

        {/* Mobile View */}
        <div className="sm:hidden flex flex-col items-center gap-4">
          <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />
          <div className="flex flex-col w-full gap-4">
            <Button
              onClick={onNext}
              disabled={isNextDisabled}
              variant="solid"
              size="md"
              rounded="lg"
              bgColor={
                isNextDisabled
                  ? "opacity-50 cursor-not-allowed"
                  : "bg-rose-500 hover:bg-rose-600"
              }
              textColor="text-white"
              className="w-full"
            >
              {currentStep === totalSteps - 1
                ? t("interactive_material.assignStudents")
                : t("common.next")}
            </Button>
            <Button
              onClick={onPrev}
              bgColor="bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
              rounded="lg"
              size="md"
              variant="solid"
              className="w-full"
            >
              {currentStep === 0 ? t("common.exit") : t("common.previous")}
            </Button>
          </div>
        </div>
      </div>
    );
  },
);

interface EditViewProps {
  id: string;
  onFinish: () => void;
}

const EditView = ({ id, onFinish }: EditViewProps) => {
  const { t } = useTranslation();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [buttonLoading, setButtonLoading] = useState(false);
  const queryClient = useQueryClient();

  const {
    data: material,
    isLoading,
    error,
    refetch,
  } = useGetInteractiveMaterial(Number(id), {
    query: {
      refetchInterval: (query) => {
        if (
          query.state.data?.status ===
          InteractiveMaterialResponseDtoStatus.INITIALIZING
        ) {
          return 5000;
        }
        return false;
      },
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    },
  });

  const steps: Step[] = useMemo(() => {
    return material?.generatorMode === "PDF"
      ? [
          "video",
          "glossary",
          "questions",
          "dragAndDrop",
          "flashcards",
          "summary",
          "review",
        ]
      : [
          "video",
          "glossary",
          "questions",
          "videoQuestions",
          "dragAndDrop",
          "flashcards",
          "summary",
          "review",
        ];
  }, [material?.generatorMode]);

  const { mutate: handleInteractiveMaterialGeneratorWebhook } =
    useHandleInteractiveMaterialGeneratorWebhook({
      mutation: {
        onSettled: () => {
          queryClient.invalidateQueries({
            queryKey: getGetInteractiveMaterialQueryKey(Number(id)),
          });
        },
      },
    });

  const { mutateAsync: finalizeInteractiveMaterial } =
    useFinalizeInteractiveMaterial({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetInteractiveMaterialQueryKey(Number(id)),
          });
        },
      },
    });

  const handleNext = useCallback(async () => {
    if (currentStepIndex === steps.length - 2) {
      try {
        setButtonLoading(true);
        await finalizeInteractiveMaterial({ id: Number(id) });
        setCurrentStepIndex(currentStepIndex + 1);
      } catch (error) {
        console.error("Failed to finalize material:", error);
      } finally {
        setButtonLoading(false);
      }
      return;
    }

    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else if (currentStepIndex === steps.length - 1) {
      onFinish();
    }
  }, [
    currentStepIndex,
    finalizeInteractiveMaterial,
    id,
    onFinish,
    steps.length,
  ]);

  const handlePrev = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    } else if (currentStepIndex === 0) {
      onFinish();
    }
  }, [currentStepIndex, onFinish]);

  const handleGenerateFlashcards = useCallback(
    (flashcards: InteractiveMaterialFlashcards[]) => {
      handleInteractiveMaterialGeneratorWebhook({
        id: Number(id),
        data: {
          flashcards: flashcards,
        },
      });
    },
    [handleInteractiveMaterialGeneratorWebhook, id],
  );

  const handleQuestionsEdit = useCallback(
    (
      editedQuestions: InteractiveMaterialResponseDtoGeneratedQuizQuestionsItem[],
    ) => {
      handleInteractiveMaterialGeneratorWebhook({
        id: Number(id),
        data: {
          quizQuestions: editedQuestions,
        },
      });
    },
    [handleInteractiveMaterialGeneratorWebhook, id],
  );

  const handleVideoQuestionsEdit = useCallback(
    (
      editedQuestions: InteractiveMaterialResponseDtoGeneratedVideoQuestionsItem[],
    ) => {
      handleInteractiveMaterialGeneratorWebhook({
        id: Number(id),
        data: {
          videoQuestions: editedQuestions,
        },
      });
    },
    [handleInteractiveMaterialGeneratorWebhook, id],
  );

  const handleGlossaryChange = useCallback(
    (newGlossaries: InteractiveMaterialGlossary[]) => {
      handleInteractiveMaterialGeneratorWebhook({
        id: Number(id),
        data: {
          glossaryItems: newGlossaries,
        },
      });
    },
    [handleInteractiveMaterialGeneratorWebhook, id],
  );

  const handleDragAndDropChange = useCallback(
    (newDragAndDropItems: DragTextItem[]) => {
      handleInteractiveMaterialGeneratorWebhook({
        id: Number(id),
        data: {
          dragAndDrops: newDragAndDropItems,
        },
      });
    },
    [handleInteractiveMaterialGeneratorWebhook, id],
  );

  const handleSummaryChange = useCallback(
    (summary: string) => {
      handleInteractiveMaterialGeneratorWebhook({
        id: Number(id),
        data: { summary },
      });
    },
    [handleInteractiveMaterialGeneratorWebhook, id],
  );

  const renderStepContent = () => {
    if (!material) return null;

    const stepComponents = {
      video: <WelcomeTab material={material} />,
      // case "transcript":
      // return <TranscriptTab material={material} />;
      glossary: (
        <GlossaryTab
          material={material}
          onGlossaryChange={handleGlossaryChange}
        />
      ),
      questions: (
        <QuizQuestionTab
          material={material}
          onQuestionsChange={handleQuestionsEdit}
        />
      ),
      videoQuestions: (
        <VideoQuestionTab
          material={material}
          onQuestionsChange={handleVideoQuestionsEdit}
        />
      ),
      dragAndDrop: (
        <DragAndDropTab
          material={material}
          onDragAndDropChange={handleDragAndDropChange}
        />
      ),
      flashcards: (
        <FlashCardTab
          material={material}
          onFlashcardsChange={handleGenerateFlashcards}
        />
      ),
      summary: (
        <SummaryTab material={material} onSummaryChange={handleSummaryChange} />
      ),
      review: <ReviewTab material={material} />,
    };

    return stepComponents[steps[currentStepIndex]];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-rose-50 dark:from-gray-900 dark:to-rose-900">
      <div className="container max-w-7xl mx-auto px-4 py-4">
        {isLoading ? (
          <LoadingContainer
            title={t("interactive_material.loading.title")}
            themeColor="rose"
            fullHeight={false}
          />
        ) : error ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : material ? (
          <div className="space-y-6 sm:space-y-8">
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50">
              <div
                className={
                  steps[currentStepIndex] === "review"
                    ? "pb-4 px-4 mt-2"
                    : "p-4 sm:p-6"
                }
              >
                {renderStepContent()}
                <StepNavigation
                  currentStep={currentStepIndex}
                  totalSteps={steps.length}
                  onNext={handleNext}
                  onPrev={handlePrev}
                  isNextDisabled={buttonLoading}
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default EditView;
